import {
  Input as ChakraInput,
  type InputProps as ChakraInputProps,
  type RecipeVariantProps,
  useRecipe,
} from '@chakra-ui/react';
import type { inputRecipe } from '@shared/chakra/themeSettings/recipes/Input.recipe';
import React from 'react';

export type InputProps = ChakraInputProps &
  RecipeVariantProps<typeof inputRecipe> & {
    invalid?: boolean;
  };

/**
 * Memoized Input to improve performance on frequent re-renders.
 * Prevents unnecessary style recalculation and re-renders unless variant props change.
 */
export const Input: React.FC<InputProps> = React.memo((props) => {
  const { invalid, ...rest } = props;
  // Destructure props for style-relevant and rest
  const recipe = useRecipe({ key: 'input' });

  // Only memoize styles when recipeVariantProps change, never on every keystroke.
  // This reduces performance cost during fast input
  // (since container and event handler closures no longer re-fire).
  const [recipeProps, restProps] = React.useMemo(
    () => recipe.splitVariantProps(rest),
    // Only re-run if recipe-related props change
    [rest]
  );

  // Memoize computed styles as well
  const styles = React.useMemo(() => recipe(recipeProps), [recipe, recipeProps]);

  // ChakraInput is fast if style objects don't change reference
  // Respect an explicit `height` passed via props; fall back to 40px otherwise.
  // Do not change this height; it affects places where this component is used.
  const resolvedHeight = (restProps as any).height ?? '40px';

  return (
    <ChakraInput
      css={styles}
      data-invalid={invalid ? '' : undefined}
      {...restProps}
      colorPalette="brand"
      height={resolvedHeight}
    />
  );
});

Input.displayName = 'Input';
